import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Switch, Space, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import request from '../utils/request';

const Departments: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  // 新增/编辑在途锁：Modal 的 onOk 不感知 promise，必须自己置 loading 防重复提交
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchList = async (p = page) => {
    setLoading(true);
    try {
      const res: any = await request.get('/department', { params: { page: p, pageSize: 10 } });
      setList(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [page]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    if (saving) return;
    setSaving(true);
    try {
      if (editItem) {
        await request.put(`/department/${editItem.id}`, values);
        message.success('更新成功');
      } else {
        await request.post('/department', values);
        message.success('创建成功');
      }
      setModalOpen(false);
      form.resetFields();
      setEditItem(null);
      fetchList();
    } finally {
      setSaving(false);
    }
  };

  // 删除：服务端已校验「科室下是否仍有医生」，这里先给出条数提示，失败信息统一由拦截器弹出
  const handleDelete = (record: any) => {
    const doctorCount = record?._count?.doctors ?? 0;
    Modal.confirm({
      title: '确认删除？',
      content: doctorCount > 0
        ? `科室「${record.name}」下仍有 ${doctorCount} 名医生，删除会被拒绝，请先调整医生归属。`
        : `科室「${record.name}」删除后不可恢复，确认删除？`,
      okButtonProps: { danger: true },
      onOk: async () => {
        await request.delete(`/department/${record.id}`);
        message.success('删除成功');
        fetchList();
      },
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '科室名称', dataIndex: 'name' },
    { title: '描述', dataIndex: 'description' },
    { title: '排序', dataIndex: 'sort', width: 80 },
    { title: '医生数', dataIndex: ['_count', 'doctors'], width: 80 },
    { title: '状态', dataIndex: 'status', width: 80, render: (v: number) => v === 1 ? '启用' : '禁用' },
    {
      title: '操作', width: 150,
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" onClick={() => { setEditItem(record); form.setFieldsValue(record); setModalOpen(true); }}>编辑</Button>
          <Button size="small" danger onClick={() => handleDelete(record)}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>科室管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditItem(null); form.resetFields(); setModalOpen(true); }}>
          新增科室
        </Button>
      </div>
      <Table columns={columns} dataSource={list} rowKey="id" loading={loading}
        pagination={{ current: page, total, pageSize: 10, onChange: setPage }} />
      <Modal title={editItem ? '编辑科室' : '新增科室'} open={modalOpen}
        onOk={handleSubmit} onCancel={() => !saving && setModalOpen(false)}
        okButtonProps={{ loading: saving }}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="科室名称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="sort" label="排序" initialValue={0}>
            <InputNumber min={0} />
          </Form.Item>
          {editItem && (
            <Form.Item name="status" label="状态" valuePropName="checked"
              getValueFromEvent={(checked: boolean) => checked ? 1 : 0}
              getValueProps={(value: number) => ({ checked: value === 1 })}>
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default Departments;
